import { drawableStepState, flowApproxFrom, flowPosition, stepKindNote } from '@/entities/booking'
import type { FlowStepState } from '@/entities/booking'
import type { BookingDetail } from '@/shared/api'
import { Icon, SectionCard } from '@/shared/ui'
import s from './BookingFlowCard.module.css'

/**
 * Состояния, у которых ЗДЕСЬ есть свой вид (CSS красит строку по `data-state`).
 *
 * Держит СБОРОЧНУЮ защиту, обещанную в `entities/booking/model/server-drift.ts`: добавят в контракт
 * четвёртое состояние шага — упадёт сборка здесь. Без неё незнакомое состояние молча рисовалось бы
 * как «впереди».
 *
 * ⚠️ С рантайм-фолбэком (`drawableStepState`) не спорит: тот про ЖИВОЙ сервер, ушедший вперёд от
 * нашей сборки, а этот — про наш код, отставший от контракта. Разные моменты времени.
 */
const STYLED_STATES = { done: true, current: true, pending: true } satisfies Record<
  FlowStepState,
  true
>

/**
 * «Ход брони» (prototype/Prototype.dc.html:2323) — где бронь сейчас и что с ней будет дальше.
 *
 * Список ПЛОСКИЙ — спека такой выбор разрешает (3.1), и плата за него одна: сказать, что при
 * ветвлении он приблизителен. Это делает плашка внизу; подробнее — `entities/booking/model/flow.ts`.
 *
 * Отвечает на три вопроса, и все три — строками СЕРВЕРА: по какому сценарию и какой ВЕРСИИ идёт
 * бронь (7.1: «странно себя ведёт» чаще всего значит «идёт по старой версии»); до какого времени
 * ждёт текущий шаг (7.1); и что дальше ничего не будет — когда сценарий закончился или остановлен
 * отменой (3.1, «вид завершения»). ⚠️ Своей догадки «раз бронь отменена, значит сценарий встал»
 * здесь нет: сценарий мог доработать ветку отмены до конца.
 */
export function BookingFlowCard({ flow }: { flow: BookingDetail['flow'] }) {
  return (
    <SectionCard className={s.card} eyebrow="Ход брони">
      <div className={s.body}>
        <FlowLead flow={flow} />

        {/* `role="list"` при `list-style: none` не избыточен, что бы ни говорил линтер: Safari с
            VoiceOver снимает со списка семантику ровно из-за снятых маркеров. Тот же приём и та же
            причина, что в `JourneyChecklist`. */}
        {/* oxlint-disable-next-line jsx-a11y/no-redundant-roles */}
        <ol className={s.steps} role="list">
          {flow.steps.map((step, index) => {
            // Единственное чтение состояния на строку — и сразу через дверь фолбэка: незнакомое
            // значение с сервера не должно оставлять строку вообще без вида (`server-drift.ts`).
            const state = drawableStepState(step.state)
            const kind = stepKindNote(step.title, step.kindLabel)
            // Тип и примечание — одной строкой. ⚠️ Примечания (время/сумма) в макете под этапом
            // НЕТ: там только название и тип. Показываем осознанно — спека требует время и сумму
            // по каждому этапу (3.1), и это ровно тот случай, когда макет беднее контракта, а не
            // мы дорисовываем состояние, которого в нём нет.
            const notes = [kind, step.note].filter(Boolean).join(' · ')

            return (
              <li key={step.key} className={s.step} data-state={STYLED_STATES[state] && state}>
                <span className={s.mark} aria-hidden="true">
                  <span className={s.dot} />
                  {/* Хвост линии тянем ко ВСЕМ строкам, кроме последней: в макете он есть у каждой,
                      и под нижним этапом остаётся обрубок, ведущий в никуда. */}
                  {index < flow.steps.length - 1 ? <span className={s.thread} /> : null}
                </span>

                <span className={s.text}>
                  <span className={s.title}>
                    {/* Пройден этап или ещё впереди — на экране это ТОЛЬКО цвет точки и подписи,
                        а цвет не единственный способ читать (WCAG 1.4.1) и вовсе не доезжает до
                        скринридера: колонка с точкой `aria-hidden`. Проговариваем словами. У
                        текущего этапа этого не нужно — у него есть настоящий текст «сейчас здесь». */}
                    {state === 'current' ? null : (
                      <span className={s.srOnly}>{state === 'done' ? 'пройден: ' : 'впереди: '}</span>
                    )}
                    {step.title}
                  </span>
                  {notes ? <span className={s.note}>{notes}</span> : null}
                </span>

                {state === 'current' ? <span className={s.here}>сейчас здесь</span> : null}
              </li>
            )
          })}
        </ol>

        {/*
         * ⚠️ Главная строка этой секции при ветвлении: плоский список показывает шаги подряд, а бронь
         * могла уйти по другой ветке — и обойдённый шаг стоит здесь как «впереди» (3.1). Список
         * выглядит точным, и менеджер ждал бы шага, которого не случится.
         *
         * У ОСТАНОВЛЕННОГО сценария плашку прячем: «дальше путь показан приблизительно» под строкой
         * «дальше бронь никуда не идёт» — обещание пути, которого нет. В макете плашка зависит только
         * от графа, но макет и не показывал остановку словами.
         */}
        <FlowApproxNote flow={flow} />
      </div>
    </SectionCard>
  )
}

/**
 * Оговорки под списком: где путь перестаёт быть точным и почему отметка может стоять не там.
 *
 * ⚠️ Главная строка секции при ветвлении. Плоский список показывает шаги подряд, но бронь могла уйти
 * по другой ветке — и тогда шаг, который она уже обошла, стоит здесь как «впереди» (3.1). Молчать
 * нельзя: список выглядит точным, и менеджер будет ждать шага, которого не случится.
 *
 * Плашка называет ГРАНИЦУ — первый ещё не пройденный этап внутри ветки (см. `flowApproxFrom`). Без
 * неё она предупреждала обо всём сразу и потому ни о чём: глядя на конкретный «Заезд», понять,
 * надёжен ли он, было нельзя. Если сервер границу не прислал (поле необязательное), остаётся общая
 * формулировка — врать про место хуже, чем не называть его.
 *
 * У ОСТАНОВЛЕННОГО сценария молчим совсем: «дальше путь показан приблизительно» под строкой «дальше
 * бронь никуда не идёт» — обещание пути, которого нет. В макете плашка зависит только от графа, но
 * макет и не показывал остановку словами.
 */
function FlowApproxNote({ flow }: { flow: BookingDetail['flow'] }) {
  if (flow.stoppedLabel || !flow.branching) return null
  const approxFrom = flowApproxFrom(flow.steps)

  return (
    <p className={s.approx}>
      <Icon name="info" size={13} strokeWidth={1.9} className={s.approxIcon} />
      <span className={s.approxText}>
        В сценарии есть развилки — дальше путь показан приблизительно
        {approxFrom ? <>, начиная с этапа «{approxFrom}»</> : null}: бронь могла уйти по другой ветке.
      </span>
    </p>
  )
}

/**
 * Строка под заголовком: где бронь, чем ведётся и чего ждёт.
 *
 * Когда сценарий встал (дошёл до конца или остановлен отменой), всё остальное неважно и способно
 * соврать: «ждём оплату» у отменённой брони — обещание, которого никто не выполнит. Поэтому
 * `stoppedLabel` не дописывается к строке, а ЗАМЕНЯЕТ её целиком.
 */
function FlowLead({ flow }: { flow: BookingDetail['flow'] }) {
  // «Не начат» — это когда НИ ОДИН этап не пройден, а не когда нет текущего: текущего нет и у
  // дошедшего до конца сценария, и у остановленного. Судить по `currentStep === 0` значило бы
  // написать «Сценарий не начат» над списком, где все этапы зелёные.
  const notStarted = flow.steps.every((step) => step.state === 'pending')
  // Номер — по самому списку (`flowPosition`), а не по серверному `currentStep`: он называет ту
  // строку, на которой стоит отметка «сейчас здесь», и разойтись им теперь нечем. Почему так —
  // в `entities/booking/model/flow.ts`.
  const here = flowPosition(flow.steps)
  const position = flow.stoppedLabel
    ? flow.stoppedLabel
    : notStarted
      ? 'Сценарий не начат'
      : here
        ? `Шаг ${here.step} из ${here.total}`
        : `Пройдено этапов: ${flow.steps.length}`

  return (
    <p className={s.lead}>
      {position}
      {/* Сценарий и версию показываем и у ОСТАНОВЛЕННОЙ брони — именно её и приходят разбирать,
          когда «странно себя ведёт», а чаще всего это значит «шла по старой версии» (7.1).
          Версия и название необязательны по отдельности: версия — несущая половина, и терять её
          из-за отсутствия названия нельзя. */}
      {flow.scenarioVersion || flow.scenarioName ? (
        <>
          {/* У остановленного сценария «идёт по» — неправда: он уже никуда не идёт. */}
          {flow.stoppedLabel ? ' · шла по ' : ' · идёт по '}
          {flow.scenarioVersion ? (
            // Моно, как все технические токены приложения: её и ищут глазами.
            <span className={s.version}>{flow.scenarioVersion}</span>
          ) : null}
          {flow.scenarioVersion && flow.scenarioName ? ' · ' : ''}
          {flow.scenarioName}
        </>
      ) : null}
      {flow.waitingFor ? ` · ждём «${flow.waitingFor}»` : ''}
      {/* Срок — сам по себе: контракт объявляет его отдельным полем, и шаг со сроком, но без
          готовой фразы «чего ждём», не должен терять срок заодно. */}
      {flow.dueLabel ? ` ${flow.dueLabel}` : ''}
    </p>
  )
}

